import { useEffect, useLayoutEffect, useState } from 'react'

/**
 * Motor de paginação real do Sumário dos templates Easy Solutions — mesma ideia do motor
 * já usado em `pdfs/bradesco/pgr/_helpers/pagination.js` (ver aquele arquivo para o "porquê"
 * detalhado de medir em vez de tentar `target-counter()`/`string-set` do CSS Paged Media:
 * verificado empiricamente como não suportado pelo motor de impressão do Chromium usado
 * neste pipeline via `page.pdf()`/impressão do navegador).
 *
 * CAUSA RAIZ QUE ISSO CORRIGE: o PR #779 envolveu cada título de seção principal (exceto
 * "1. Introdução", que já começa no topo da página por causa do `pageBreakAfter` do
 * Sumário) com `<View pageBreakBefore>` (`components/pdf/View.js`) para consertar um
 * problema de seções ficando "coladas" — mas isso move CADA seção para o topo de uma
 * página nova, e os números de página do Sumário (`summaryItems` em cada `Content.js`)
 * continuaram cravados nos valores de antes do PR #779. Como o conteúdo de cada seção
 * também é dinâmico (nº de perguntas respondidas, nº de fotos anexadas — `renderQuestion`/
 * `renderImages` nos `Content.js`), NENHUM valor fixo se mantém correto para toda
 * Atividade: um valor só bate por coincidência (poucas fotos) e diverge para outra
 * (Atividade com mais fotos empurra as seções seguintes mais para frente). Foi exatamente
 * isso que a Julia reportou (pontos 2, 3 e 4 do Sumário apontando pra página 3, mas o PDF
 * real já não tinha mais nada desses pontos ali).
 *
 * POR QUE ISTO É MAIS SIMPLES QUE O DO BRADESCO/PGR: os documentos Easy Solutions não têm
 * `<table>`/blocos com `page-break-inside: avoid` no corpo (só o `<table>` de layout de
 * `components/pdf/Page.js`, que não usa `avoid`) nem seções em paisagem — então não é
 * preciso simular o "empurrão" de bloco atômico do motor de fragmentação do Chromium.
 * A única coisa que este módulo precisa modelar (e que o do bradesco/pgr não precisa) é o
 * `pageBreakBefore` explícito do PR #779: ao contrário de um bloco "avoid" (que só é
 * empurrado SE cruzaria uma página), aqui o empurrão é INCONDICIONAL — toda seção marcada
 * começa numa página nova, mesmo que coubesse inteira no espaço restante da página atual.
 * Isso torna o cálculo exato (aritmética, não heurística de "cruzaria a fronteira?").
 *
 * PREMISSA DE LARGURA/MARGEM: igual ao bradesco/pgr, isto só funciona porque
 * `components/pdf/Preview.js` renderiza os templates na tela com a mesma largura de
 * impressão (`maxWidth: '21cm'`, A4 retrato) — o texto quebra em linha igual ao impresso.
 * Diferente do bradesco/pgr, aqui a margem de impressão real ESTÁ declarada no próprio
 * código (`_shared/PageMargins.js`: `@page { margin: 3cm 2cm 2cm 3cm }`), então não é uma
 * estimativa — é o valor que o Chromium de fato usa para paginar.
 *
 * LIMITAÇÃO CONHECIDA: assim como no bradesco/pgr, nenhum motor de impressão quebra uma
 * linha de texto no meio — se a última linha de uma página não coubesse inteira, ela desce
 * inteira para a próxima. Este módulo não modela essa "sobra" de até uma linha por página
 * (não há, neste ambiente, uma Atividade real nem um pipeline de impressão para calibrar
 * um resíduo como o `PORTRAIT_RESIDUAL_MARGIN_MM` do bradesco/pgr) — o erro acumulado
 * disso tende a ser pequeno (uma fração de linha por página, e cada página aqui tem ~24-25
 * linhas de corpo de texto), mas pode, em documentos muito longos, atrasar em 1 página um
 * número calculado perto do fim. Ver observação no PR para o que fazer se isso for
 * observado em QA: um único ajuste (constante) aqui corrige todos os templates de uma vez.
 */

const MM_PER_INCH = 25.4
const PX_PER_INCH = 96
const MM_TO_PX = PX_PER_INCH / MM_PER_INCH
const CM_TO_PX = MM_TO_PX * 10

const PAGE_HEIGHT_MM = 297 // A4 retrato
// `_shared/PageMargins.js`: `@page { margin: 3cm 2cm 2cm 3cm }` (topo/direita/base/esquerda).
const PAGE_MARGIN_TOP_CM = 3
const PAGE_MARGIN_BOTTOM_CM = 2
const PAGE_HEIGHT_PX = PAGE_HEIGHT_MM * MM_TO_PX - (PAGE_MARGIN_TOP_CM + PAGE_MARGIN_BOTTOM_CM) * CM_TO_PX

// Margem de segurança (em px) contra ruído de sub-pixel do `getBoundingClientRect()` ao
// comparar uma posição com uma fronteira de página exata.
const PAGE_BOUNDARY_EPSILON_PX = 0.5

// A Capa (`<View pageBreakAfter>`) é a página 1, o Sumário (também `pageBreakAfter`) é a
// página 2 — o conteúdo medido aqui (a partir de `BODY_ORIGIN_ID`) sempre começa na 3.
const FIRST_BODY_PAGE_NUMBER = 3

// Âncora "zero" da medição: marcador de altura zero logo após o Sumário se fechar, antes
// do primeiro título de seção ("1. Introdução") — ver cada `Content.js`.
export const BODY_ORIGIN_ID = 'es-pagination-origin'

// Header/Footer (`sections/Header.js`/`Footer.js`) se repetem em toda página impressa
// (`components/pdf/Page.js` os renderiza em `<thead>`/`<tfoot>` com
// `table-header-group`/`table-footer-group`), mas na tela (sem paginação real) aparecem
// só uma vez — sua altura precisa ser descontada manualmente do espaço útil de cada
// página impressa.
export const HEADER_CHROME_ID = 'es-header-chrome'
export const FOOTER_CHROME_ID = 'es-footer-chrome'

const getTop = id => {
  const el = document.getElementById(id)
  return el ? el.getBoundingClientRect().top : null
}

/**
 * Para cada âncora marcada com `pageBreakBefore` (em ordem de documento), calcula o
 * deslocamento acumulado necessário para empurrá-la ao topo da próxima página impressa —
 * incondicionalmente, mesmo que ela já coubesse na página atual. Esse deslocamento passa a
 * valer para tudo que vem depois dela no fluxo, até a próxima âncora forçada.
 */
const buildForcedBreakShifts = (forcedAnchorIds, originTop, effectivePageHeight) => {
  const shifts = []
  let shift = 0

  forcedAnchorIds.forEach(id => {
    const top = getTop(id)
    if (top === null) return

    const rawTop = top - originTop
    const linearTop = rawTop + shift
    const pushedTop =
      Math.ceil(linearTop / effectivePageHeight - PAGE_BOUNDARY_EPSILON_PX / effectivePageHeight) *
      effectivePageHeight

    shift = pushedTop - rawTop
    shifts.push({ rawTop, shift })
  })

  return shifts
}

// Deslocamento acumulado válido numa posição bruta (não deslocada) — o último ponto de
// corte cuja posição já foi alcançada no fluxo (inclui o próprio ponto de corte, para que
// a âncora forçada receba o deslocamento que ela mesma provocou).
const shiftAt = (shifts, rawTop) => {
  let shift = 0
  for (let i = 0; i < shifts.length; i++) {
    if (shifts[i].rawTop > rawTop + PAGE_BOUNDARY_EPSILON_PX) break
    shift = shifts[i].shift
  }
  return shift
}

const computePageNumbers = (anchorIds, forcedAnchorIds) => {
  const originTop = getTop(BODY_ORIGIN_ID)
  if (originTop === null) return {}

  const headerHeight = document.getElementById(HEADER_CHROME_ID)?.getBoundingClientRect().height || 0
  const footerHeight = document.getElementById(FOOTER_CHROME_ID)?.getBoundingClientRect().height || 0
  const effectivePageHeight = PAGE_HEIGHT_PX - headerHeight - footerHeight
  if (!(effectivePageHeight > 0)) return {}

  const shifts = buildForcedBreakShifts(forcedAnchorIds, originTop, effectivePageHeight)

  const result = {}
  anchorIds.forEach(id => {
    const top = getTop(id)
    if (top === null) return
    const rawTop = Math.max(0, top - originTop)
    const printedTop = rawTop + shiftAt(shifts, rawTop)
    const pageIndex = Math.floor(printedTop / effectivePageHeight + PAGE_BOUNDARY_EPSILON_PX / effectivePageHeight)
    result[id] = FIRST_BODY_PAGE_NUMBER + pageIndex
  })
  return result
}

/**
 * Mede a posição real (após o layout) de cada âncora em `anchorIds` e devolve um mapa
 * `{ [id]: numeroDaPagina }`. `forcedAnchorIds` é a sublista (em ordem de documento) das
 * âncoras que têm `<View pageBreakBefore>` — precisa estar em ordem para o cálculo do
 * deslocamento acumulado fazer sentido. IDs cuja âncora não existe/não pôde ser medida
 * simplesmente não aparecem no mapa — os `Content.js` que usam este hook tratam a
 * ausência da entrada como "página ainda não conhecida" (não desenham nada), nunca um
 * placeholder tipo "0".
 *
 * Mede de novo após o `load` da janela: imagens do documento (logo, fotos anexadas às
 * respostas) podem terminar de carregar depois do primeiro layout e mudar a altura do
 * conteúdo.
 */
export const useEasySolutionsPageNumbers = (anchorIds, forcedAnchorIds) => {
  const [pageNumbers, setPageNumbers] = useState({})
  const anchorKey = anchorIds.join('|')
  const forcedKey = forcedAnchorIds.join('|')

  useLayoutEffect(() => {
    setPageNumbers(computePageNumbers(anchorIds, forcedAnchorIds))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, forcedKey])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const recompute = () => setPageNumbers(computePageNumbers(anchorIds, forcedAnchorIds))
    window.addEventListener('load', recompute)
    return () => window.removeEventListener('load', recompute)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, forcedKey])

  return pageNumbers
}
